import { Injectable } from '@angular/core';
import {AbstractDatabaseService} from "../../tandem-core/services/abstract-database.service";
import {SpendingPlan} from "../models/spending-plan";
import {AngularFirestore} from "@angular/fire/compat/firestore";
import {TandemUser} from "../../auth/models/tandem-user";

@Injectable({
  providedIn: 'root'
})
export class SpendingPlanService extends AbstractDatabaseService<SpendingPlan>{
  factor: number = 1.33; // Adjust this factor to change the step difference

  constructor(protected override afs: AngularFirestore) {
    super('spendingPlans', afs);
  }

  getTrackingToolName(variant: 'single' | 'plural', user: TandemUser | null): string {
    if (variant === 'single') {
      return user?.customTTMetadata?.spendingPlanName ? user.customTTMetadata.spendingPlanName : 'Spending Plan';
    } else {
      return user?.customTTMetadata?.spendingPlanNamePlural ? `${user.customTTMetadata.spendingPlanNamePlural}` : 'Spending Plans';
    }
  }

  getTrackingToolDescription(user: TandemUser | null) {
    return !!user?.customTTMetadata?.spendingPlanDescription ? user.customTTMetadata.spendingPlanDescription : 'A prioritized plan for allocating the cash you expect to bring in over a period of time.'
  }

  public generateChartDataForSpendingPlan(spendingPlan: SpendingPlan, omitLabels: boolean = false) {

    let redIndex = 0;
    let greenIndex = 0;
    let darkRedIndex = 0;
    let darkGreenIndex = 0;

    const redColors = this.generateShades('#D5212E', 30);
    const darkRedColors = this.generateShades('#A10000', 30);
    const greenColors = this.generateShades('#8bc34a', 30);
    const darkGreenColors = this.generateShades('#558b2f', 30);
    const categoryColors: string[] = [];
    const categoryLabels: string[] = [];
    const categoryTotals: number[] = [];
    const sectionColors: string[] = [];
    const sectionLabels: string[] = [];
    const sectionTotals: number[] = [];

    spendingPlan.revenueSections.forEach(revSection => {
      sectionLabels.push(revSection.name);
      sectionColors.push(darkGreenColors[darkGreenIndex++]);
      let sectionTotal = 0;
      revSection.categories.filter(c => c.total > 0).forEach(category => {
        sectionTotal += category.total;
        categoryLabels.push(category.name);
        categoryTotals.push(category.total);
        categoryColors.push(greenColors[greenIndex++]);
      });
      sectionTotals.push(sectionTotal);
    });

    spendingPlan.expenseSections.forEach(expSection => {
      sectionLabels.push(expSection.name);
      sectionColors.push(darkRedColors[darkRedIndex++]);
      let sectionTotal = 0;
      expSection.categories.filter(c => c.total > 0).forEach(category => {
        sectionTotal += category.total;
        categoryLabels.push(category.name);
        categoryTotals.push(category.total);
        categoryColors.push(redColors[redIndex++]);
      });
      sectionTotals.push(sectionTotal);
    });

    sectionTotals.forEach(total => categoryColors.push('')); // Offsets coloring of chart to match colors up

    const data: any = {
      labels: omitLabels ? [] : [...sectionLabels, ...categoryLabels],
      datasets: [
        {
          label: 'Total Value',
          labels: categoryLabels,
          data: categoryTotals,
          backgroundColor: categoryColors,
        },
        {
          label: 'Total Value',
          data: sectionTotals,
          backgroundColor: sectionColors,
        }
      ],
    };
    console.log(data)
    return data;
  }

  public getNumberOfSections(spendingPlan: SpendingPlan): number {
    let total = 0;
    if (spendingPlan) {
      total += spendingPlan.revenueSections.filter(sec => sec.categories.reduce((acc, val) => acc + val.total, 0) > 0).length;
      total += spendingPlan.expenseSections.filter(sec => sec.categories.reduce((acc, val) => acc + val.total, 0) > 0).length;
    }
    return total;
  }

  generateShades(startColor: string, count: number): string[] {
    const startRed = parseInt(startColor.substring(1, 3), 16);
    const startGreen = parseInt(startColor.substring(3, 5), 16);
    const startBlue = parseInt(startColor.substring(5, 7), 16);

    const endRed = 255;
    const endGreen = 255;
    const endBlue = 255;

    let shades: string[] = [];

    for (let i = 0; i < count; i++) {
      const interpolatedRed = this.interpolateColor(startRed, endRed, i, count);
      const interpolatedGreen = this.interpolateColor(startGreen, endGreen, i, count);
      const interpolatedBlue = this.interpolateColor(startBlue, endBlue, i, count);

      shades.push(`#${this.componentToHex(interpolatedRed)}${this.componentToHex(interpolatedGreen)}${this.componentToHex(interpolatedBlue)}`);
    }

    return shades;
  }

  private interpolateColor(start: number, end: number, step: number, maxStep: number): number {
    const delta = (end - start) / (maxStep - 1) * this.factor;
    const result = start + delta * step;

    // Ensure result is within [0, 255] range
    return Math.min(255, Math.max(0, Math.round(result)));
  }

  private componentToHex(c: number): string {
    const hex = c.toString(16);
    return hex.length == 1 ? "0" + hex : hex;
  }
}
